import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(localStorage.getItem('token'));

    const checkAuth = useCallback(async () => {
        // Skip auth check if we're in the middle of OAuth callback
        if (window.location.hash?.includes('session_id=')) {
            setLoading(false);
            return;
        }
        
        // Don't make API call if no token exists - reduces unnecessary 401 errors
        const storedToken = localStorage.getItem('token');
        if (!storedToken) {
            setLoading(false);
            setUser(null);
            return;
        }

        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
        } catch (error) {
            setUser(null);
            localStorage.removeItem('token');
            setToken(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = useCallback(async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
        setUser(response.data.user);
        setToken(response.data.token);
        localStorage.setItem('token', response.data.token);
        return response.data;
    }, []);

    const register = useCallback(async (name, email, password) => {
        const response = await axios.post(`${API}/auth/register`, { name, email, password });
        setUser(response.data.user);
        setToken(response.data.token);
        localStorage.setItem('token', response.data.token);
        return response.data;
    }, []);

    const loginWithGoogle = useCallback(() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + '/auth/callback';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, []);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (e) {
            // Ignore errors
        }
        setUser(null);
        setToken(null);
        localStorage.removeItem('token');
    }, []);

    const processOAuthCallback = useCallback(async (sessionId) => {
        const response = await axios.get(`${API}/auth/session`, {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true
        });
        setUser(response.data.user);
        // Store session token for subsequent requests
        if (response.data.session_token) {
            localStorage.setItem('token', response.data.session_token);
            setToken(response.data.session_token);
        }
        return response.data;
    }, []);

    const contextValue = useMemo(() => ({
        user,
        loading,
        token,
        login,
        register,
        loginWithGoogle,
        logout,
        processOAuthCallback,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin'
    }), [user, loading, token, login, register, loginWithGoogle, logout, processOAuthCallback]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};
